/**
 * NFT Gallery Components
 * Displays NFT metadata and information
 */

import React, { useState, useEffect } from "react";
import { ethers } from "ethers";
import { getWalletBrowserProvider } from "../../services/wallet-provider";
// import { contracts } from "../../config";
import { useDispatch, useSelector } from "react-redux";
import { connectWallet } from "../../store/wallet/wallet-actions";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import Ptdc from "../utility/ptdc";

// eslint-disable-next-line no-undef
const serverUrl = process.env.REACT_APP_SERVER_URL;

/**
 * NFTGallery Component
 * A React component that displays user's NFTs from multiple collections
 * Handles wallet connection, NFT loading, and metadata display
 */

/**
 * Main NFTGallery component
 * Fetches and displays NFTs owned by the connected wallet address
 * @returns {JSX.Element} Rendered NFT gallery component
 */
const NFTGallery = () => {
  /**
   * State management
   */
  const [nftsByCollection, setNftsByCollection] = useState({});
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [collectionNames, setCollectionNames] = useState({});
  const { isConnected, isConnecting, address, chainId, error: walletError } = useSelector((state) => state.wallet);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const config = useSelector((state) => state.config);
  const [contracts, setContracts] = useState([]);

  /**
   * Smart contract interface definition
   * Defines the functions and events needed for NFT interaction
   */
  const CONTRACT_ABI = [
    "function balanceOf(address owner) view returns (uint256)",
    "function tokenURI(uint256 tokenId) view returns (string)",
    "function ownerOf(uint256 tokenId) view returns (address)",
    "function name() view returns (string)",
    "event Transfer(address indexed from, address indexed to, uint256 indexed tokenId)",
  ];

  /**
   * Fetches and processes NFT metadata from IPFS or HTTP URLs
   * @param {string} uri - The URI pointing to the NFT metadata
   * @returns {Promise<Object|null>} Parsed metadata object or null if fetch fails
   */
  const fetchMetadata = async (uri) => {
    try {
      let url = uri;
      if (!url.endsWith(".json")) {
        url = `${url}.json`;
      }

      if (url.startsWith("ipfs://")) {
        url = url.replace("ipfs://", "https://ipfs.io/ipfs/");
      }

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const metadata = await response.json();
      return metadata;
    } catch (error) {
      return null;
    }
  };

  const handleNFTClick = (nft, collectionName, contractAddress) => {
    navigate(`/nft/${contractAddress}/${nft.tokenId}`, {
      state: {
        nft,
        collectionName,
        contractAddress,
      },
    });
  };

  /**
   * Effect hook to load NFTs when wallet is connected
   */
  useEffect(() => {
    if (isConnected) {
      loadNFTs();
    }
  }, [isConnected, address, chainId, config]);

  /**
   * Loads all NFTs owned by the connected wallet across all configured collections
   * Fetches token IDs, URIs, and metadata for each NFT
   * @async
   * @throws {Error} When MetaMask is not installed or when NFT loading fails
   */
  const loadNFTs = async () => {
    try {
      setLoading(true);
      setErrors({});

      const provider = await getWalletBrowserProvider();
      const allNfts = {};
      const names = {};
      if (config?.clientConfig?.data?.collections) {
        const response = await axios.post(`${serverUrl}/api/collections`, {
          addresses: config.clientConfig.data.collections,
        });

        const contractList = response.data.data.map(
          (collection) => collection.address,
        );

        setContracts(contractList);
        // console.log("contracts", contracts);

        for (const contractAddress of contractList) {
          try {
            const contract = new ethers.Contract(
              contractAddress,
              CONTRACT_ABI,
              provider,
            );

            const collectionName = await contract.name();
            names[contractAddress] = collectionName;

            const balance = await contract.balanceOf(address);

            if (balance === 0) {
              allNfts[contractAddress] = [];
              continue;
            }

            const filter = contract.filters.Transfer(null, address);
            const events = await contract.queryFilter(filter);
            const uniqueTokenIds = new Set();

            for (const event of events.reverse()) {
              const tokenId = event.args[2];
              try {
                const currentOwner = await contract.ownerOf(tokenId);
                if (currentOwner.toLowerCase() === address.toLowerCase()) {
                  uniqueTokenIds.add(tokenId.toString());
                }
              } catch (error) {
                continue;
              }
            }

            const nftDetails = [];
            for (const tokenId of uniqueTokenIds) {
              try {
                // eslint-disable-next-line no-undef
                const numericTokenId = BigInt(tokenId);
                const uri = await contract.tokenURI(numericTokenId);
                const metadata = await fetchMetadata(uri);
                nftDetails.push({
                  tokenId: tokenId,
                  tokenURI: uri,
                  metadata: metadata,
                });
              } catch (error) {
                nftDetails.push({
                  tokenId: tokenId,
                  tokenURI: null,
                  metadata: null,
                });
              }
            }

            allNfts[contractAddress] = nftDetails;
          } catch (error) {
            setErrors((prev) => ({
              ...prev,
              [contractAddress]: "Error loading NFTs: " + error.message,
            }));
          }
        }
      }

      setNftsByCollection(allNfts);
      setCollectionNames(names);
      setLoading(false);
    } catch (error) {
      setErrors((prev) => ({
        ...prev,
        general: "Error loading NFTs: " + error.message,
      }));
      setLoading(false);
    }
  };

  const truncateDescription = (text, maxLength) => {
    const description = text || "";
    return description.length > maxLength
      ? description.substring(0, maxLength) + "..."
      : description;
  };

  /**
   * Render wallet connection screen when not connected
   */
  if (!isConnected) {
    return (
      <div className="nft-gallery-page public-centered-panel max-w-md mx-auto mt-8 p-6 bg-white rounded-lg shadow-md">
        <button
          disabled={isConnecting}
          onClick={() => dispatch(connectWallet())}
          className="cta cta-standard w-full"
        >
          {isConnecting ? "Connessione…" : "Connect Wallet"}
        </button>
        {walletError && <p role="alert">{walletError}</p>}
        {errors.general && (
          <div className="public-state public-state-error mt-4 p-4 bg-red-50 text-red-500 rounded-lg">
            {errors.general}
          </div>
        )}
      </div>
    );
  }

  /**
   * Main render section
   * Displays NFT collections, loading states, and error messages
   */
  return (
    <div className="nft-gallery-page container-cust mx-auto homepage">
      <div className="nft-gallery-header grid grid-cols-12">
        <h1 className="col-span-12">I tuoi prodotti</h1>
        <p className="col-span-12">
          Connected wallet: {address?.slice(0, 6)}...{address?.slice(-4)}
        </p>
        {/* {myProducts.map((prod) => (
          <div className="col-span-12 sm:col-span-6 lg:col-span-4">
            <ProductPreview key={prod.id} prod={prod} />
          </div>
        ))} */}
      </div>

      {loading ? (
        <div className="public-state public-state-loading flex justify-center items-center h-32">
          <div className="text-lg text-gray-600">Loading...</div>
        </div>
      ) : errors.general ? (
        <div className="public-state public-state-error p-4 bg-red-50 text-red-500 rounded-lg">
          {errors.general}
        </div>
      ) : Object.keys(nftsByCollection).length === 0 ? (
        <div className="public-state public-state-empty text-center p-8 bg-gray-50 rounded-lg">
          <p className="text-gray-600">No NFT collections found</p>
        </div>
      ) : (
        <>
          {contracts.map((contractAddress) => (
            <section className="nft-collection-group" key={contractAddress}>
              {errors[contractAddress] ? (
                <div className="public-state public-state-error p-4 bg-red-50 text-red-500 rounded-lg">
                  {errors[contractAddress]}
                </div>
              ) : nftsByCollection[contractAddress]?.length === 0 ? (
                <div className="public-state public-state-empty text-center p-4">
                  <p className="text-gray-600">
                    {"You don't own any NFTs from this collection yet"}
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-12 gap-6">
                  {nftsByCollection[contractAddress]?.map((nft) => (
                    <div
                      key={`${contractAddress}-${nft.tokenId}`}
                      className="nft-card-cell col-span-12 sm:col-span-6 lg:col-span-4"
                    >
                      {nft.metadata?.image && (
                        <article className="nft-card product-preview">
                          <div className="image-container">
                            <img
                              src={nft.metadata.image}
                              alt={nft.metadata.name || `Token ${nft.tokenId}`}
                            />
                          </div>
                          <div className="text-container">
                            <div className="details-container">
                              <div className="detail">ID:</div>
                              <div className="detail">000/000</div>
                            </div>
                            <Ptdc
                              // pretitle={"Tipologia: " + prod.type}
                              title={
                                nft.metadata?.name || `Token ${nft.tokenId}`
                              }
                              description={truncateDescription(
                                nft.metadata.description,
                                170,
                              )}
                            />
                            <button
                              className="cta cta-standard"
                              onClick={() =>
                                handleNFTClick(
                                  nft,
                                  collectionNames[contractAddress],
                                  contractAddress,
                                )
                              }
                            >
                              Vedi il prodotto
                            </button>
                          </div>
                        </article>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </section>
          ))}
        </>
      )}

      <div className="mt-8 flex justify-center">
        <button onClick={loadNFTs} className="cta cta-standard">
          Refresh
        </button>
      </div>
    </div>
  );
};

export default NFTGallery;
